import React, { useState, useEffect } from "react";
import { Button, Form, Table } from "react-bootstrap";
import { Autocomplete, IconButton, Box } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {
  getModelsBySupplierID,
  getProductsBySupplierAndModelID,
} from "slices/Procurement/purchase-order/DirectPO/thunk";
import { fetchProductById } from "slices/Masters/product/thunk";
import { MecordColors } from "Constants/Color";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { useFormik } from "formik";
import * as Yup from "yup";
import EditButton from "Common/Components/Buttons/EditButton";
import { DeleteButton } from "Common/Components/Buttons/DeleteButton";
import { autocompleteDropdownStyles } from "Constants/Style";
import { showCustomSwal } from "helpers/swalHelper";
import { Add } from "@mui/icons-material";
import { TruncatedTextWithTooltip } from "Common/Components/TruncatedTextWithTooltip";

interface FormValues {
  model: any | null;
  product: any | null;
  productInfo: any | null;
  quantity: number | string;
  product_code: string;
  description: string;
  remark: string;
}

interface ProductFormTableProps {
  mainFormik: any;
  isWithPR: boolean;
  calculateGSTValues: (gstPercent: number) => {
    cgst_percent: number;
    sgst_percent: number;
    igst_percent: number;
  };
  pr_supplier_data: any;
  onRemoveProduct?: (product: any) => void;
}

const ProductFormTable: React.FC<ProductFormTableProps> = ({
  mainFormik,
  isWithPR,
  calculateGSTValues,
  pr_supplier_data,
  onRemoveProduct,
}) => {
  const dispatch = useDispatch<any>();
  const [modelOptions, setModelOptions] = useState<any[]>([]);
  const [productOptions, setProductOptions] = useState<any[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [editableRowId, setEditableRowId] = useState<string | null>(null);

  const supplierId = mainFormik.values.supplier_id;
  const exchangeRate = Number(mainFormik.values.exchange_rate) || 1;
  const currencySymbol = mainFormik.values.currency?.currency_symbol || "";

  useEffect(() => {
    const loadModels = async () => {
      if (!supplierId) {
        setModelOptions([]);
        return;
      }
      setLoadingModels(true);
      try {
        const res = await dispatch(getModelsBySupplierID(supplierId)).unwrap();
        setModelOptions(res?.data || []);
      } catch (err) {
        toast.error("Failed to fetch models");
      } finally {
        setLoadingModels(false);
      }
    };
    loadModels();
  }, [dispatch, supplierId]);

  const fetchProducts = async (modelId: string) => {
    if (!supplierId || !modelId) return;
    setLoadingProducts(true);
    try {
      const res = await dispatch(
        getProductsBySupplierAndModelID({ supplierId, modelId }),
      ).unwrap();
      setProductOptions(res?.data || []);
    } catch (err) {
      toast.error("Failed to fetch products");
    } finally {
      setLoadingProducts(false);
    }
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      model: null,
      product: null,
      productInfo: null,
      quantity: 1,
      product_code: "",
      description: "",
      remark: "",
    },
    validationSchema: Yup.object({
      model: Yup.object().nullable().required("Model is required"),
      product: Yup.object().nullable().required("Product is required"),
      quantity: Yup.number()
        .required("Quantity is required")
        .min(1, "Quantity must be at least 1"),
      product_code: Yup.string().required("Product Code is required"),
    }),
    onSubmit: async (values) => {
      const selectedProduct = values.product;
      if (!selectedProduct) return;

      try {
        const unitPrice = Number(selectedProduct?.supplier_unit_price) || 0;
        const quantity = Number(values.quantity);
        const gstPercent =
          pr_supplier_data?.supplier_type.toLowerCase() === "local"
            ? Number(selectedProduct?.gst_percent) || 0
            : 0;

        const roundToTwo = (num: number) => Math.round(num * 100) / 100;

        const rateInCurrency = roundToTwo(unitPrice / exchangeRate);
        const itemAmount = roundToTwo(rateInCurrency * quantity);
        const gstValues = calculateGSTValues(gstPercent);

        // Use effective GST percent from breakdown
        const effectiveGstPercent =
          gstValues.cgst_percent +
          gstValues.sgst_percent +
          gstValues.igst_percent;
        const gstAmount = roundToTwo((itemAmount * effectiveGstPercent) / 100);
        const total = roundToTwo(itemAmount + gstAmount);

        const newProduct = {
          product_id: selectedProduct.product_id,
          product_name: selectedProduct.product_name,
          product_code: values.product_code,
          product_details: selectedProduct,
          unit_name: selectedProduct?.unit?.unit_name,
          unit_id: selectedProduct?.unit?.unit_id,
          unit_price: unitPrice,
          order_quantity: quantity,
          rate_in_currency: rateInCurrency,
          item_amount: itemAmount,
          net_taxable_value: itemAmount,
          gst_percent: gstPercent,
          ...gstValues,
          gst_amount: gstAmount,
          total: total,
          description: values.description,
          remark: values.remark,
          delivery_lead_time: selectedProduct?.delivery_lead_time,
        };

        mainFormik.setFieldValue("products", [
          ...mainFormik.values.products,
          newProduct,
        ]);
        formik.resetForm();
        setProductOptions([]);
      } catch (err) {
        toast.error("Failed to add product");
      }
    },
  });

  const handleDelete = async (index: number) => {
    const productName = mainFormik.values.products[index]?.product_name;
    const result = await showCustomSwal({
      icon: "warning",
      title: "Delete Product",
      text: `Are you sure you want to delete ${productName}?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
    });

    if (result.isConfirmed) {
      const updatedProducts = [...mainFormik.values.products];
      const removedProduct = updatedProducts[index];
      updatedProducts.splice(index, 1);
      mainFormik.setFieldValue("products", updatedProducts);

      if (onRemoveProduct) {
        onRemoveProduct(removedProduct);
      }

      // New Logic for remove PR from autocomplete
      if (isWithPR && removedProduct?.pr_id) {
        const prId = removedProduct?.pr_id;
        const stillExists = updatedProducts.some((p: any) => p.pr_id === prId);
        if (!stillExists) {
          const updatePRs = mainFormik.values.prs.filter(
            (id: any) => id !== prId,
          );
          mainFormik.setFieldValue("prs", updatePRs);
        }
      }
    }
  };

  const handleRowUpdate = (index: number, field: string, value: any) => {
    const updatedProducts = [...mainFormik.values.products];
    const item = { ...updatedProducts[index] };

    const roundToTwo = (num: number) => Math.round(num * 100) / 100;

    const recalcFromUnitPriceAndQty = (unitPrice: number, qty: number) => {
      const rateInCurrency = roundToTwo(unitPrice / exchangeRate);
      const itemAmount = roundToTwo(rateInCurrency * qty);
      const effectiveGstPercent =
        (item.cgst_percent || 0) +
        (item.sgst_percent || 0) +
        (item.igst_percent || 0);
      const gstAmount = roundToTwo((itemAmount * effectiveGstPercent) / 100);
      const total = roundToTwo(itemAmount + gstAmount);
      return { rateInCurrency, itemAmount, gstAmount, total };
    };

    if (field === "unit_price") {
      const newUnitPrice = Number(value) || 0;
      item.unit_price = newUnitPrice;
      const qty = Number(item.order_quantity) || 0;
      const calc = recalcFromUnitPriceAndQty(newUnitPrice, qty);
      item.rate_in_currency = calc.rateInCurrency;
      item.item_amount = calc.itemAmount;
      item.net_taxable_value = calc.itemAmount;
      item.gst_amount = calc.gstAmount;
      item.total = calc.total;
    } else if (field === "order_quantity") {
      const newQty = Number(value) || 0;
      item.order_quantity = newQty;
      const unitPrice = Number(item.unit_price) || 0;
      const calc = recalcFromUnitPriceAndQty(unitPrice, newQty);
      item.rate_in_currency = calc.rateInCurrency;
      item.item_amount = calc.itemAmount;
      item.net_taxable_value = calc.itemAmount;
      item.gst_amount = calc.gstAmount;
      item.total = calc.total;
    } else if (field === "product_code") {
      item.product_code = value;
    } else if (field === "description") {
      item.description = value;
    } else if (field === "remark") {
      item.remark = value;
    } else if (field === "delivery_lead_time") {
      item.delivery_lead_time = value;
    }

    updatedProducts[index] = item;
    mainFormik.setFieldValue("products", updatedProducts);
  };

  useEffect(() => {
    // When supplier changes → reset direct product form
    formik.resetForm();
    setModelOptions([]);
    setProductOptions([]);
  }, [supplierId]);

  const items = mainFormik.values.products || [];

  return (
    <div className="mb-4" id="products">
      <h6 className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>
        {isWithPR ? "Product Details" : "Product Selection"}
      </h6>
      {!isWithPR && (
        <div
          className="p-3 rounded bg-opacity-10 mb-2"
          style={{
            background: MecordColors?.LightThemeBGColor,
            border: `1px solid ${MecordColors?.LightThemeBGColor}`,
          }}
        >
          <Form>
            <div className="row g-3">
              <div className="col-md-3">
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Model <span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    options={modelOptions}
                    loading={loadingModels}
                    getOptionLabel={(option: any) => option.model_name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?.model_id === value?.model_id
                    }
                    value={formik.values.model}
                    onChange={(_, newValue) => {
                      formik.setFieldValue("model", newValue);
                      formik.setFieldValue("product", null);
                      formik.setFieldValue("product_code", "");
                      if (newValue) {
                        fetchProducts(newValue.model_id);
                      } else {
                        setProductOptions([]);
                      }
                    }}
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt) => opt.model_name,
                      )
                    }
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder="Select Model"
                        size="small"
                        error={
                          formik.touched.model && Boolean(formik.errors.model)
                        }
                        helperText={
                          formik.touched.model &&
                          (formik.errors.model as string)
                        }
                        sx={{ ...autocompleteDropdownStyles.inputSx }}
                      />
                    )}
                  />
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Product <span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    options={productOptions}
                    loading={loadingProducts}
                    getOptionLabel={(option: any) => option.product_name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?.product_id === value?.product_id
                    }
                    value={formik.values.product}
                    disabled={!formik.values.model || loadingProducts}
                    onChange={(_, newValue) => {
                      formik.setFieldValue("product", newValue);
                      formik.setFieldValue("productInfo", newValue);
                    }}
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt) => opt.product_name,
                      )
                    }
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder={
                          formik.values.model
                            ? "Select Product"
                            : "Select Model first"
                        }
                        size="small"
                        error={
                          formik.touched.product &&
                          Boolean(formik.errors.product)
                        }
                        helperText={
                          formik.touched.product &&
                          (formik.errors.product as string)
                        }
                        sx={{ ...autocompleteDropdownStyles.inputSx }}
                      />
                    )}
                  />
                </Form.Group>
              </div>
              <div className="col-md-2">
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Quantity <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="quantity"
                    placeholder="Qty"
                    value={formik.values.quantity}
                    onChange={formik.handleChange}
                    isInvalid={
                      formik.touched.quantity && !!formik.errors.quantity
                    }
                    style={{ height: "38px" }}
                  />
                </Form.Group>
              </div>
              <div className="col-md-2">
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Product Code <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="product_code"
                    placeholder="Enter Product Code"
                    value={formik.values.product_code}
                    onChange={formik.handleChange}
                    isInvalid={
                      formik.touched.product_code &&
                      !!formik.errors.product_code
                    }
                    style={{ height: "38px" }}
                  />
                </Form.Group>
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <Button
                  variant="primary"
                  onClick={() => formik.handleSubmit()}
                  className="w-100 d-flex align-items-center justify-content-center gap-2"
                  style={{
                    backgroundColor: MecordColors.PrimaryButtonBGColor,
                    borderColor: MecordColors.PrimaryButtonBGColor,
                    borderRadius: "8px",
                    height: "38px",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor =
                      MecordColors.PrimaryButtonHoverBGColor;
                    e.currentTarget.style.borderColor =
                      MecordColors.PrimaryButtonHoverBGColor;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor =
                      MecordColors.PrimaryButtonBGColor;
                    e.currentTarget.style.borderColor =
                      MecordColors.PrimaryButtonBGColor;
                  }}
                >
                  <Add fontSize="small" /> Add
                </Button>
              </div>
              {isWithPR && (
                <div className="col-md-6 mt-2">
                  <Form.Group>
                    <Form.Label className="fw-semibold">Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={1}
                      name="description"
                      placeholder="Enter description"
                      value={formik.values.description}
                      onChange={formik.handleChange}
                    />
                  </Form.Group>
                </div>
              )}
              <div className="col-md-6 mt-2">
                <Form.Group>
                  <Form.Label className="fw-semibold">Remark</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={1}
                    name="remark"
                    placeholder="Enter remark"
                    value={formik.values.remark}
                    onChange={formik.handleChange}
                  />
                </Form.Group>
              </div>
            </div>
          </Form>
        </div>
      )}

      <div
        className="table-responsive"
        style={{
          background: "#FFFFFF",
          border: "1px solid #DDE1EF",
          borderRadius: "5px",
        }}
      >
        <Table responsive className="align-middle text-center">
          <thead style={{ backgroundColor: MecordColors?.LightGrayColor }}>
            <tr>
              <th style={{ minWidth: "70px" }}>Sr. No</th>
              {isWithPR && <th style={{ minWidth: "120px" }}>PR No.</th>}
              <th style={{ minWidth: "150px" }}>Product Name</th>
              <th style={{ minWidth: "120px" }}>Product Code</th>
              <th style={{ minWidth: "150px" }}>Lead Time (weeks)</th>
              <th style={{ minWidth: "120px" }}>Order Qty</th>
              <th style={{ minWidth: "120px" }}>Unit</th>
              <th style={{ minWidth: "140px" }}>Unit Price</th>
              <th style={{ minWidth: "140px" }}>Rate in Currency</th>
              <th style={{ minWidth: "140px" }}>Gross Value</th>
              <th style={{ minWidth: "100px" }}>GST%</th>
              <th style={{ minWidth: "100px" }}>CGST%</th>
              <th style={{ minWidth: "100px" }}>SGST%</th>
              <th style={{ minWidth: "100px" }}>IGST%</th>
              <th style={{ minWidth: "120px" }}>GST Amount</th>
              <th style={{ minWidth: "120px" }}>Total</th>
              <th style={{ minWidth: "250px" }}>PR Description</th>
              <th style={{ minWidth: "250px" }}>Remark</th>
              <th
                style={{
                  minWidth: "120px",
                  textAlign: "center",
                  position: "sticky",
                  right: 0,
                  backgroundColor: MecordColors?.LightGrayColor,
                  zIndex: 10,
                }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={16} className="text-center py-4 text-muted">
                  No products added yet.
                </td>
              </tr>
            ) : (
              items.map((prod: any, idx: number) => {
                const rowId = `row-${idx}`;
                return (
                  <tr key={rowId}>
                    <td>{idx + 1}</td>
                    {isWithPR && (
                      <td>{prod?.pr_no ?? prod?.purchase_request?.pr_no}</td>
                    )}
                    <td>
                      <TruncatedTextWithTooltip
                        text={prod.product_name}
                        maxWidth={"200px"}
                      />
                    </td>
                    <td>
                      {editableRowId === rowId ? (
                        <Form.Control
                          size="sm"
                          value={prod.product_code}
                          onChange={(e) =>
                            handleRowUpdate(idx, "product_code", e.target.value)
                          }
                        />
                      ) : (
                        prod.product_code
                      )}
                    </td>
                    <td className="text-center">
                      {editableRowId === rowId ? (
                        <Form.Control
                          size="sm"
                          value={prod.delivery_lead_time}
                          onChange={(e) =>
                            handleRowUpdate(
                              idx,
                              "delivery_lead_time",
                              e.target.value,
                            )
                          }
                        />
                      ) : (
                        prod.delivery_lead_time
                      )}
                    </td>
                    <td className="text-center">
                      {editableRowId === rowId ? (
                        <Form.Control
                          type="number"
                          size="sm"
                          value={prod.order_quantity}
                          onChange={(e) =>
                            handleRowUpdate(
                              idx,
                              "order_quantity",
                              e.target.value,
                            )
                          }
                        />
                      ) : (
                        prod.order_quantity
                      )}
                    </td>
                    <td>{prod.unit_name}</td>
                    <td className="text-end">
                      {editableRowId === rowId ? (
                        <Form.Control
                          type="number"
                          size="sm"
                          value={prod.unit_price}
                          onChange={(e) =>
                            handleRowUpdate(idx, "unit_price", e.target.value)
                          }
                          style={{ minWidth: "90px" }}
                        />
                      ) : (
                        <>
                          {"₹"} {Number(prod.unit_price).toFixed(2)}
                        </>
                      )}
                    </td>
                    <td className="text-end">
                      {currencySymbol}{" "}
                      {Number(prod.rate_in_currency).toFixed(2)}
                    </td>
                    <td className="text-end">
                      {currencySymbol} {Number(prod.item_amount).toFixed(2)}
                    </td>
                    <td className="text-center">{prod.gst_percent}%</td>
                    <td className="text-center">{prod.cgst_percent}%</td>
                    <td className="text-center">{prod.sgst_percent}%</td>
                    <td className="text-center">{prod.igst_percent}%</td>
                    <td className="text-end">
                      {currencySymbol} {Number(prod.gst_amount).toFixed(2)}
                    </td>
                    <td className="text-end">
                      {currencySymbol} {Number(prod.total).toFixed(2)}
                    </td>
                    <td>
                      {editableRowId === rowId && isWithPR ? (
                        <Form.Control
                          size="sm"
                          value={prod.description}
                          onChange={(e) =>
                            handleRowUpdate(idx, "description", e.target.value)
                          }
                        />
                      ) : (
                        <TruncatedTextWithTooltip
                          text={prod.description}
                          maxWidth={"250px"}
                        />
                      )}
                    </td>
                    <td>
                      {editableRowId === rowId ? (
                        <Form.Control
                          size="sm"
                          value={prod.remark}
                          onChange={(e) =>
                            handleRowUpdate(idx, "remark", e.target.value)
                          }
                        />
                      ) : (
                        <TruncatedTextWithTooltip
                          text={prod.remark}
                          maxWidth={"250px"}
                        />
                      )}
                    </td>
                    <td
                      className="text-center"
                      style={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#fff",
                        zIndex: 1,
                      }}
                    >
                      <div className="d-flex justify-content-center gap-2">
                        {editableRowId === rowId ? (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => setEditableRowId(null)}
                              sx={{ color: MecordColors.ThemeBGColor }}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => setEditableRowId(null)}
                              sx={{ color: "#6c757d" }}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <EditButton
                              onClick={() => setEditableRowId(rowId)}
                              iconClassName="ph-pencil"
                            />
                            <DeleteButton
                              onClick={() => handleDelete(idx)}
                              iconClassName="ph-trash"
                            />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default ProductFormTable;
